import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import { X } from 'lucide-react';

interface RequestFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RequestFormDialog = ({ open, onOpenChange }: RequestFormDialogProps) => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    telegram: '',
    project: '',
  });

  const handleTelegramChange = (value: string) => {
    // Ensure @ prefix for telegram
    if (value && !value.startsWith('@')) {
      value = '@' + value.replace(/^@+/, '');
    }
    setFormData({ ...formData, telegram: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Open Telegram with pre-filled message
    const message = encodeURIComponent(
      `Hi! I'd like to request a smart contract audit.\n\nName: ${formData.name}\nContact: ${formData.contact}\nTelegram: ${formData.telegram}\nProject: ${formData.project}`
    );
    window.open(`https://t.me/alexdolgavin?text=${message}`, '_blank');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-card border-border p-0 overflow-hidden">
        <div className="relative p-8 md:p-10">
          {/* Close button */}
          <button 
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} />
          </button>

          <DialogHeader className="mb-10 text-left">
            <DialogTitle className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              <span className="text-gradient-red">Contact us</span>
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-muted-foreground text-base mb-3">Your name</label>
              <Input
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
                required
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Phone or E-mail</label>
              <Input
                placeholder="+1 234 567 8900 or email@example.com"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Your Telegram</label>
              <Input
                placeholder="@telegram"
                value={formData.telegram}
                onChange={(e) => handleTelegramChange(e.target.value)}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Project name</label>
              <Input
                placeholder="Your project name or URL"
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
                required
              />
            </div>

            <Button type="submit" variant="outline" className="rounded-full h-14 px-10 text-base mt-4">
              Leave a request
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestFormDialog;
